/**
 * Web Storage access that cannot take down app boot.
 *
 * Storage goes missing in two distinct ways, and a `typeof window` check
 * catches neither:
 *
 *   - a runtime that exposes a DOM but no Web Storage, and
 *   - a browser where the `localStorage` property access *itself* throws
 *     `SecurityError` because site data is blocked (private modes,
 *     cookie-blocking settings, sandboxed iframes). Reads can throw too.
 *
 * That matters most for module-scope reads — Redux slices build their initial
 * state while being imported, so an unguarded read there fails the whole app's
 * boot rather than one feature, and no React error boundary can catch it.
 *
 * Callers that must distinguish "unreadable" from "absent" should use
 * {@link getSafeStorage} and read from it inside their own try/catch: the
 * convenience readers below flatten a failed read to `null`, which is
 * indistinguishable from a missing key.
 */

/** The storage object, or null when it is unavailable or access throws. */
export const getSafeStorage = (
  kind: "local" | "session" = "local",
): Storage | null => {
  try {
    if (kind === "session") {
      return globalThis.sessionStorage === undefined ? null : sessionStorage;
    }
    return globalThis.localStorage === undefined ? null : localStorage;
  } catch {
    return null;
  }
};

/** Read a key, or null when storage is unavailable, throws, or has no value. */
export const readSafeStorage = (
  key: string,
  kind: "local" | "session" = "local",
): string | null => {
  try {
    return getSafeStorage(kind)?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

/** Write a key. Returns whether the value was actually persisted. */
export const writeSafeStorage = (
  key: string,
  value: string,
  kind: "local" | "session" = "local",
): boolean => {
  try {
    const storage = getSafeStorage(kind);
    if (!storage) return false;
    storage.setItem(key, value);
    return true;
  } catch {
    // Also covers QuotaExceededError, not just blocked storage.
    return false;
  }
};

export type SafeStorageChange = {
  key: string;
  value: string | null;
};

/**
 * Apply related key changes as one logical commit. Web Storage has no
 * transaction API, so a failed operation is rolled back to the snapshot read
 * immediately before the commit. Rollback is best effort when the browser
 * rejects every write.
 */
export const applySafeStorageChanges = (
  changes: SafeStorageChange[],
  kind: "local" | "session" = "local",
): boolean => {
  const storage = getSafeStorage(kind);
  if (!storage) return false;

  let previousValues: Array<[string, string | null]> = [];
  try {
    previousValues = changes.map(({ key }) => [key, storage.getItem(key)]);
    for (const { key, value } of changes) {
      if (value === null) {
        storage.removeItem(key);
      } else {
        storage.setItem(key, value);
      }
    }
    return true;
  } catch {
    for (const [key, value] of previousValues) {
      try {
        if (value === null) {
          storage.removeItem(key);
        } else {
          storage.setItem(key, value);
        }
      } catch {
        // The original commit already failed; rollback is best effort.
      }
    }
    return false;
  }
};

/** Remove a key and report whether the removal reached storage. */
export const removeSafeStorage = (
  key: string,
  kind: "local" | "session" = "local",
): boolean => {
  try {
    const storage = getSafeStorage(kind);
    if (!storage) return false;
    storage.removeItem(key);
    return true;
  } catch {
    return false;
  }
};
